// New product gallery with slide effect
class ProductGallery {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.gallery = document.querySelector(`#gallery-${sectionId}`);
    this.slidesContainer = this.gallery ? this.gallery.querySelector('.s-main-product__slides-container') : null;
    this.thumbnails = this.gallery ? this.gallery.parentElement.querySelectorAll('.s-main-product__thumbnail') : [];
    this.slides = this.gallery ? this.gallery.querySelectorAll('.s-main-product__slide') : [];
    this.prevBtn = document.querySelector(`#prev-${sectionId}`);
    this.nextBtn = document.querySelector(`#next-${sectionId}`);
    this.galleryPrevBtn = document.querySelector(`#gallery-prev-${sectionId}`);
    this.galleryNextBtn = document.querySelector(`#gallery-next-${sectionId}`);
    this.thumbnailsWrapper = document.querySelector(`#thumbnails-wrapper-${sectionId}`);
    
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isTransitioning = false; // Prevent multiple rapid clicks
    
    // Properties for swipe/drag
    this.startX = 0;
    this.startY = 0;
    this.endX = 0;
    this.endY = 0;
    this.isDragging = false;
    this.minSwipeDistance = 50; // Minimum distance to consider a swipe
    
    this.init();
  }
  
  init() {
    if (!this.gallery || this.totalSlides === 0) return;
    
    this.bindEvents();
    this.bindSwipeEvents();
    this.bindVideoEvents();
    this.updateNavigationState();
    this.updateSlidePosition(); // Initialize slide position
  }
  
  bindEvents() {
    // Click on thumbnails
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });
    
    // Navigation with arrows (thumbnails)
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.previousSlide();
      });
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.nextSlide();
      });
    }
    
    // Navigation with gallery arrows
    if (this.galleryPrevBtn) {
      this.galleryPrevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.previousSlide();
      });
    }
    
    if (this.galleryNextBtn) {
      this.galleryNextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
      });
    }
  }
  
  bindSwipeEvents() {
    if (!this.gallery) return;
    
    // Touch events for mobile devices only
    this.gallery.addEventListener('touchstart', (e) => {
      this.handleTouchStart(e);
    }, { passive: true });
    
    this.gallery.addEventListener('touchmove', (e) => {
      this.handleTouchMove(e);
    }, { passive: true });
    
    this.gallery.addEventListener('touchend', (e) => {
      this.handleTouchEnd(e);
    }, { passive: true });
    
    // Prevent the default behavior of dragging images
    this.gallery.addEventListener('dragstart', (e) => {
      e.preventDefault();
    });
  }
  
  bindVideoEvents() {
    // Handle video play buttons within this gallery instance
    const playButtons = this.gallery ? this.gallery.querySelectorAll('.js-play-button') : [];
    const videoOverlays = this.gallery ? this.gallery.querySelectorAll('.js-video-overlay') : [];
    
    playButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.playVideo(button);
      });
    });
    
    videoOverlays.forEach((overlay) => {
      overlay.addEventListener('click', (e) => {
        e.stopPropagation();
        const playButton = overlay.querySelector('.js-play-button');
        if (playButton) {
          this.playVideo(playButton);
        }
      });
    });
  }
  
  playVideo(playButton) {
    const videoContainer = playButton.closest('.s-main-product__video-container');
    const video = videoContainer.querySelector('.s-main-product__video');
    
    if (video && videoContainer) {
      // Add playing class to hide overlay
      videoContainer.classList.add('playing');
      
      // Play the video
      video.play();
      
      // Remove existing event listeners to avoid duplicates
      video.removeEventListener('pause', this.handleVideoPause);
      video.removeEventListener('ended', this.handleVideoEnded);
      video.removeEventListener('click', this.handleVideoClick);
      
      // Create bound functions for proper context
      this.handleVideoPause = () => {
        // Only show overlay if video was paused manually (not programmatically)
        if (video.currentTime > 0 && !video.ended && video.paused) {
          setTimeout(() => {
            if (video.paused && video.currentTime > 0 && !video.ended) {
              videoContainer.classList.remove('playing');
            }
          }, 100); // Small delay to ensure it was a user action
        }
      };
      
      this.handleVideoEnded = () => {
        videoContainer.classList.remove('playing');
        video.currentTime = 0; // Reset to beginning
      };
      
      this.handleVideoClick = (e) => {
        // Allow video controls to work normally, don't prevent default
        e.stopPropagation(); // Just prevent gallery navigation
      };
      
      // Add event listeners
      video.addEventListener('pause', this.handleVideoPause);
      video.addEventListener('ended', this.handleVideoEnded);
      video.addEventListener('click', this.handleVideoClick);
    }
  }
  
  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;
    
    // Prevent multiple rapid clicks during transition
    if (this.isTransitioning) return;
    
    // Set transitioning flag
    this.isTransitioning = true;
    
    // Pause any playing media in the current slide
    this.pauseCurrentMedia();
    
    // Remove active class from the current slide
    this.slides[this.currentIndex].classList.remove('active');
    this.thumbnails[this.currentIndex].classList.remove('active');
    
    this.currentIndex = index;
    
    // Add active class to the new slide
    this.slides[this.currentIndex].classList.add('active');
    this.thumbnails[this.currentIndex].classList.add('active');
    
    // Update slide position with transform
    this.updateSlidePosition();
    
    // Update navigation state
    this.updateNavigationState();
    
    // Smooth scroll to the active thumbnail
    this.scrollToActiveThumbnail();
    
    // Reset transitioning flag after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 400); // Match the CSS transition duration
  }
  
  updateSlidePosition() {
    if (!this.slidesContainer) return;
    
    // Use transform for slide effect on both desktop and mobile
    const translateX = -this.currentIndex * 100;
    this.slidesContainer.style.transform = `translateX(${translateX}%)`;
    
    // Ensure all slides are visible for the slide effect
    this.slides.forEach((slide) => {
      slide.style.display = '';
    });
  }
  
  pauseCurrentMedia() {
    const currentSlide = this.slides[this.currentIndex];
    if (currentSlide) {
      const videoContainer = currentSlide.querySelector('.s-main-product__video-container');
      const video = currentSlide.querySelector('.s-main-product__video');
      
      if (video && videoContainer) {
        video.pause();
        videoContainer.classList.remove('playing');
        video.currentTime = 0; // Reset to beginning
        
        // Remove event listeners to prevent memory leaks
        if (this.handleVideoPause) {
          video.removeEventListener('pause', this.handleVideoPause);
        }
        if (this.handleVideoEnded) {
          video.removeEventListener('ended', this.handleVideoEnded);
        }
        if (this.handleVideoClick) {
          video.removeEventListener('click', this.handleVideoClick);
        }
      }
    }
  }
  
  previousSlide() {
    // Prevent multiple rapid clicks and check boundaries
    if (this.isTransitioning || this.currentIndex <= 0) return;
    
    this.goToSlide(this.currentIndex - 1);
  }
  
  nextSlide() {
    // Prevent multiple rapid clicks and check boundaries
    if (this.isTransitioning || this.currentIndex >= this.totalSlides - 1) return;
    
    this.goToSlide(this.currentIndex + 1);
  }
  
  updateNavigationState() {
    const isFirst = this.currentIndex === 0;
    const isLast = this.currentIndex === this.totalSlides - 1;
    
    // Update thumbnail navigation buttons
    if (this.prevBtn) {
      this.prevBtn.disabled = isFirst;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = isLast;
    }
    
    // Update gallery navigation buttons
    if (this.galleryPrevBtn) {
      this.galleryPrevBtn.disabled = isFirst;
    }
    
    if (this.galleryNextBtn) {
      this.galleryNextBtn.disabled = isLast;
    }
  }
  
  scrollToActiveThumbnail() {
    const activeThumbnail = this.thumbnails[this.currentIndex];
    if (activeThumbnail && this.thumbnailsWrapper) {
      const containerRect = this.thumbnailsWrapper.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();
      
      // Check if the thumbnail is visible
      const isVisible = (
        thumbnailRect.left >= containerRect.left &&
        thumbnailRect.right <= containerRect.right
      );
      
      if (!isVisible) {
        // Calculate the optimal scroll position without creating unnecessary whitespace
        const thumbnailOffsetLeft = activeThumbnail.offsetLeft;
        const thumbnailWidth = activeThumbnail.offsetWidth;
        const containerWidth = this.thumbnailsWrapper.offsetWidth;
        const currentScrollLeft = this.thumbnailsWrapper.scrollLeft;
        
        let targetScrollLeft;
        
        // If thumbnail is to the left of the visible area
        if (thumbnailRect.left < containerRect.left) {
          targetScrollLeft = thumbnailOffsetLeft;
        }
        // If thumbnail is to the right of the visible area
        else if (thumbnailRect.right > containerRect.right) {
          // Try to center, but don't exceed the maximum scroll
          const maxScrollLeft = this.thumbnailsWrapper.scrollWidth - containerWidth;
          const centerPosition = thumbnailOffsetLeft - (containerWidth - thumbnailWidth) / 2;
          targetScrollLeft = Math.min(centerPosition, maxScrollLeft);
        }
        
        if (targetScrollLeft !== undefined) {
          this.thumbnailsWrapper.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        }
      }
    }
  }
  
  // Touch event handlers
  handleTouchStart(e) {
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.isDragging = true;
  }
  
  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    this.endX = e.touches[0].clientX;
    this.endY = e.touches[0].clientY;
  }
  
  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.handleSwipe();
  }
  
  
  // Logic for swipe detection
  handleSwipe() {
    const deltaX = this.endX - this.startX;
    const deltaY = this.endY - this.startY;
    
    // Check if the horizontal movement is valid
    if (Math.abs(deltaX) < this.minSwipeDistance) return;
    
    // Check if the horizontal movement is greater than the vertical (swipe horizontal)
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;
    
    // Determine the direction of the swipe
    if (deltaX > 0) {
      // Swipe to the right - previous slide
      this.previousSlide();
    } else {
      // Swipe to the left - next slide
      this.nextSlide();
    }
  }
}

// Initialize all galleries when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initAllProductGalleries();
  initAllNutritionalModals();
});

// Initialize also for pages with dynamic loading
document.addEventListener('shopify:section:load', (event) => {
  // For dynamically loaded sections, initialize only the specific section
  const sectionId = event.target.dataset.sectionId;
  if (sectionId) {
    new ProductGallery(sectionId);
    initNutritionalModal(sectionId);
  } else {
    // Fallback: initialize all
    initAllProductGalleries();
    initAllNutritionalModals();
  }
});

// Function to initialize all product galleries
function initAllProductGalleries() {
  const galleries = document.querySelectorAll('.js-main-product__gallery');
  galleries.forEach(gallery => {
    const sectionId = gallery.id.replace('gallery-', '');
    if (sectionId) {
      // Check if gallery is already initialized to prevent duplicates
      if (!gallery.dataset.initialized) {
        new ProductGallery(sectionId);
        gallery.dataset.initialized = 'true';
      }
    }
  });
}

// Function to initialize all nutritional modals
function initAllNutritionalModals() {
  const nutritionalBtns = document.querySelectorAll('.js-view-nutritional-label');
  nutritionalBtns.forEach(btn => {
    const sectionId = btn.id.replace('nutritional-btn-', '');
    if (sectionId) {
      initNutritionalModal(sectionId);
    }
  });
}

// Modal for nutritional label
function initNutritionalModal(sectionId) {
  const nutritionalBtn = document.querySelector(`#nutritional-btn-${sectionId}`);
  const nutritionalModal = document.getElementById(`nutritional-modal-${sectionId}`);
  const modalOverlay = nutritionalModal?.querySelector('.s-main-product__modal-overlay');
  const closeBtn = nutritionalModal?.querySelector('.s-main-product__modal-close');
  const closeBtnMobile = nutritionalModal?.querySelector('.s-main-product__modal-close-mobile');

  if (!nutritionalBtn || !nutritionalModal) return;

  // Remove any existing event listeners to prevent duplicates
  const newBtn = nutritionalBtn.cloneNode(true);
  nutritionalBtn.parentNode.replaceChild(newBtn, nutritionalBtn);

  // Open the modal
  newBtn.addEventListener('click', () => {
    nutritionalModal.hidden = false;
    document.body.style.overflow = 'hidden';
  });

  // Close the modal
  const closeModal = () => {
    nutritionalModal.hidden = true;
    document.body.style.overflow = '';
  };

  // Events to close - desktop button (SVG)
  if (closeBtn) {
    const newCloseBtn = closeBtn.cloneNode(true);
    closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
    newCloseBtn.addEventListener('click', closeModal);
  }

  // Events to close - mobile button (text)
  if (closeBtnMobile) {
    const newCloseBtnMobile = closeBtnMobile.cloneNode(true);
    closeBtnMobile.parentNode.replaceChild(newCloseBtnMobile, closeBtnMobile);
    newCloseBtnMobile.addEventListener('click', closeModal);
  }

  if (modalOverlay) {
    const newModalOverlay = modalOverlay.cloneNode(true);
    modalOverlay.parentNode.replaceChild(newModalOverlay, modalOverlay);
    newModalOverlay.addEventListener('click', closeModal);
  }

  // Close with ESC - use a namespaced event listener
  const escHandler = (e) => {
    if (e.key === 'Escape' && !nutritionalModal.hidden) {
      closeModal();
    }
  };
  
  // Store the handler for potential cleanup
  nutritionalModal._escHandler = escHandler;
  document.addEventListener('keydown', escHandler);
}